/**
 * MOODI Phase 7 — Product Experience
 *
 * Two small, self-contained custom elements. Neither keeps its own
 * copy of "the truth": both read live from the product form's own
 * inputs (or, for the addon line, from Shopify's own cart response),
 * so nothing here can drift out of sync with what will actually be
 * submitted at Add to Bag.
 *
 * - <moodi-embroidery-panel>  progressive disclosure + character count
 *                             + submit-time validation for the optional
 *                             embroidery fields, and (only if the
 *                             product configured a real priced addon
 *                             variant) adding that variant as a second,
 *                             authoritative cart line after the main
 *                             add-to-cart request succeeds.
 * - <moodi-config-summary>    read-only live summary, derived from the
 *                             form's own state via a MutationObserver
 *                             on Dawn's own `[data-selected-variant]`
 *                             JSON data island (the same island Dawn's
 *                             variant-selects component already keeps
 *                             fresh on every variant change, regardless
 *                             of picker type) plus the embroidery
 *                             panel's own fields.
 *
 * Respects prefers-reduced-motion: the only "motion" here is a CSS
 * max-height reveal on the embroidery fields, and that transition is
 * disabled globally in moodi-product-experience.css under
 * @media (prefers-reduced-motion: reduce) — nothing is animated from
 * this file.
 */

/**
 * MOODI Fix 07 — Real variant-matrix reconciliation.
 *
 * REPLACES Fix 06 entirely (both of its layers are removed). Fix 06's
 * design relied on Shopify's `option_values` endpoint auto-resolving an
 * *omitted* option axis to "the first available value for that option"
 * once a stale Edition was dropped from the request. Real-browser
 * testing on the dev theme falsified that assumption for this theme's
 * section-rendering path: `product.selected_or_first_available_variant`
 * came back null even though a real, available combination existed
 * (confirmed directly against `snippets/buy-buttons.liquid`, which only
 * renders "Unavailable" when that value is null). Sending Dawn an
 * incomplete option_values request is not safe here, so Fix 07 never
 * does that: it guarantees the DOM already holds a complete, real,
 * available Size + Edition pair *before* Dawn's own listener ever reads
 * the selection.
 *
 * Source of truth: a small JSON matrix of this product's actual
 * Shopify variants (id, available, and the real option-value id for
 * every option), rendered once per section by
 * snippets/product-variant-picker.liquid as
 * `<script data-moodi-variant-matrix>` inside each product's own
 * <variant-selects>. Nothing here infers compatibility from numbers,
 * labels, or variant order — every lookup below is a membership check
 * against that real data.
 *
 * Event ordering (why Dawn always receives a complete pair, and why no
 * second request/event is needed):
 *
 *   1. Customer changes a non-Edition option (currently: Size).
 *   2. This is a single capture-phase `change` listener bound once on
 *      `document`. A capture-phase listener on an ancestor always runs
 *      BEFORE a bubble-phase listener on that same ancestor, because
 *      capture fires on the way down to the event target and bubble
 *      fires on the way back up — and assets/global.js's VariantSelects
 *      registers its own listener with `addEventListener('change', ...)`
 *      (bubble phase, no capture flag). So this handler is guaranteed to
 *      run first, on every single change, with no race.
 *   3. It reads the product's real variant matrix (scoped to the exact
 *      <variant-selects> instance the event happened inside — safe even
 *      if more than one product-info/variant-selects exists on the
 *      page, e.g. a quick-view).
 *   4. It checks whether (newly selected non-Edition value(s) + the
 *      customer's CURRENTLY checked Edition) is a real, available
 *      variant. If yes, nothing is changed — the customer's Edition is
 *      preserved exactly as-is, including for products where an
 *      Edition happens to be valid under more than one Size.
 *   5. If not, it finds the first real, available variant compatible
 *      with the newly selected non-Edition value(s) (in the product's
 *      own variant order — never a hardcoded numeric range) and sets
 *      *that* Edition's radio input's `.checked = true`. Because all
 *      Edition radios share one `name` (see
 *      snippets/moodi-edition-selector.liquid), the browser's native
 *      radio-group exclusivity unchecks whatever Edition was checked
 *      before — no manual uncheck call is needed or made.
 *   6. Setting `.checked` via script does NOT dispatch a `change` or
 *      `input` event by itself (only real user interaction or `.click()`
 *      does), so no second event, no second fetch, and no possibility
 *      of a Size↔Edition ping-pong is introduced here.
 *   7. The capture phase finishes and the ORIGINAL change event
 *      continues on its normal path to the bubble phase, where
 *      VariantSelects reads `selectedOptionValues` — by now BOTH the
 *      new Size and a real, available Edition are checked, so Dawn
 *      always builds a complete `option_values=<size_id>,<edition_id>`
 *      request. Shopify then resolves a real variant through its normal
 *      lifecycle: `product.selected_or_first_available_variant`,
 *      `[data-selected-variant]`, the hidden `.product-variant-id`, and
 *      the buy button all update themselves exactly as they would for
 *      any ordinary, fully-specified variant change. Nothing here
 *      writes to the buy button, the hidden variant id, or the cart
 *      form directly — Dawn's own lifecycle is left completely
 *      untouched, only the DOM state it reads is corrected beforehand.
 *
 * An Edition-initiated change is left completely alone (guarded by an
 * early return): the Edition selector already disables incompatible
 * values server-side, so Dawn always receives a complete pair for that
 * case on its own, and Size must never be auto-changed in response.
 *
 * Loop-safety: bound exactly once per page load via the
 * `window.__moodiVariantMatrixReconcilerBound` guard, as a single
 * `document`-level delegated listener — it is never attached to any
 * node inside <variant-selects>, so Dawn replacing that subtree on
 * every section re-render cannot leave a stale or duplicate listener
 * behind, and there is nothing to re-bind after a re-render.
 */
(function () {
  if (window.__moodiVariantMatrixReconcilerBound) return;
  window.__moodiVariantMatrixReconcilerBound = true;

  function optionNameFor(target) {
    if (!target || !target.dataset) return null;
    if (target.tagName === 'SELECT') {
      return target.selectedOptions[0] ? target.selectedOptions[0].dataset.optionName : null;
    }
    return target.dataset.optionName || null;
  }

  // Reads this product's real variant matrix, scoped to the exact
  // <variant-selects> instance the change happened inside.
  function readMatrix(variantSelects) {
    const script = variantSelects.querySelector('[data-moodi-variant-matrix]');
    if (!script) return null;
    try {
      const parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  // The option value ids of every currently checked/selected option
  // OTHER than Edition — mirrors assets/global.js's own
  // VariantSelects.selectedOptionValues query so it stays in lockstep
  // with whatever Dawn itself is about to read.
  function checkedNonEditionOptionValueIds(variantSelects) {
    const nodes = Array.from(
      variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
    );
    return nodes
      .filter((el) => (el.dataset ? el.dataset.optionName : null) !== 'Edition')
      .map((el) => (el.dataset ? el.dataset.optionValueId : null))
      .filter(Boolean);
  }

  function editionOptionValueIdSet(variantSelects) {
    const ids = Array.from(variantSelects.querySelectorAll('[data-option-name="Edition"]'))
      .map((el) => (el.dataset ? el.dataset.optionValueId : null))
      .filter(Boolean);
    return new Set(ids);
  }

  function variantMatchesIds(variant, requiredIds) {
    return (
      Array.isArray(variant.optionValueIds) &&
      requiredIds.every((id) => variant.optionValueIds.indexOf(id) !== -1)
    );
  }

  document.addEventListener(
    'change',
    (event) => {
      const target = event.target;
      const variantSelects = target && target.closest && target.closest('variant-selects');
      if (!variantSelects) return;

      const changedOptionName = optionNameFor(target);

      // --- FIX 08: direct Edition selection -> authoritative Dawn variant sync -----
      // Root cause: Dawn's own VariantSelects.updateSelectionMetadata() (assets/global.js,
      // out of scope) does:
      //   const selectedSwatchValue = target.closest('.product-form__input').querySelector(...)
      // with NO null-guard. Every native Dawn option fieldset (Size, etc., rendered by
      // snippets/product-variant-picker.liquid) carries the `product-form__input` class, so
      // `.closest('.product-form__input')` resolves there. MOODI's Edition fieldset is rendered
      // by the out-of-scope snippets/moodi-edition-selector.liquid and does NOT carry that class,
      // so for an Edition-originated change event `.closest('.product-form__input')` returns null
      // and Dawn's listener throws an uncaught TypeError before it reaches
      // dispatchProductSelectEvent()/publish(optionValueSelectionChange). That means
      // ProductInfo.handleOptionValueChange (assets/product-info.js, out of scope) never runs, no
      // section-rendered fetch happens, and updateVariantInputs() never writes the real variant id
      // into input[name="id"] — so the UI/summary visually show the new Edition while Dawn's
      // authoritative variant state (and therefore Add to Cart) silently stays on the old variant.
      //
      // Fix: transiently give the Edition fieldset the `product-form__input` class for exactly the
      // duration of this synchronous change-event dispatch (capture phase, which runs before
      // Dawn's own bubble-phase listener), then remove it on the next microtask — after Dawn's
      // listener, publish(), ProductInfo.handleOptionValueChange, and the synchronous portion of
      // its fetch() call have all run, but before the browser's next paint. The class is never
      // present during a rendered frame, so there is no visual/layout side effect (the
      // `product-form__input` CSS width rule never becomes visible) and no risk of double-adding
      // across repeated events, since Dawn replaces this fieldset's subtree via viewTransition on
      // every change anyway (updateOptionValues), and this handler reapplies fresh each time via
      // the existing document-level delegated listener. This does not touch
      // snippets/moodi-edition-selector.liquid, assets/global.js, assets/product-info.js, or any
      // CSS file, and does not alter the Size-change reconciliation logic below (which continues
      // to run only for non-Edition changes, per the early-return immediately after this block).
      if (changedOptionName === 'Edition') {
        const editionFieldset = target.closest('fieldset');
        if (editionFieldset && !editionFieldset.classList.contains('product-form__input')) {
          editionFieldset.classList.add('product-form__input');
          queueMicrotask(() => {
            editionFieldset.classList.remove('product-form__input');
          });
        }
      }
      // --- end FIX 08 ---------------------------------------------------------------

      if (!changedOptionName || changedOptionName === 'Edition') return;

      const matrix = readMatrix(variantSelects);
      if (!matrix || matrix.length === 0) return;

      const nonEditionIds = checkedNonEditionOptionValueIds(variantSelects);
      if (nonEditionIds.length === 0) return;

      const editionChecked = variantSelects.querySelector('[data-option-name="Edition"]:checked');
      const currentEditionId = editionChecked ? editionChecked.dataset.optionValueId : null;

      // 1) Preserve the customer's current Edition if that exact,
      // complete pair is a real, available variant.
      if (currentEditionId) {
        const preserved = matrix.find(
          (variant) => variant.available && variantMatchesIds(variant, nonEditionIds.concat(currentEditionId))
        );
        if (preserved) return;
      }

      // 2) Otherwise, the first real, available variant compatible with
      // the newly selected non-Edition value(s), in the product's own
      // real variant order.
      const fallback = matrix.find((variant) => variant.available && variantMatchesIds(variant, nonEditionIds));
      if (!fallback) return;

      const editionIds = editionOptionValueIdSet(variantSelects);
      const fallbackEditionId = fallback.optionValueIds.find((id) => editionIds.has(id));
      if (!fallbackEditionId) return;

      const radioToCheck = variantSelects.querySelector(
        '[data-option-name="Edition"][data-option-value-id="' + fallbackEditionId + '"]'
      );
      if (!radioToCheck || radioToCheck.checked) return;

      // Native radio-group exclusivity (shared `name`) unchecks whatever
      // Edition was checked before. No event is dispatched here — the
      // original Size change event, still on its way to the bubble
      // phase, carries this corrected, complete state into Dawn.
      radioToCheck.checked = true;
    },
    true
  );
})();

if (!customElements.get('moodi-embroidery-panel')) {
  customElements.define(
    'moodi-embroidery-panel',
    class MoodiEmbroideryPanel extends HTMLElement {
      connectedCallback() {
        this.toggles = Array.from(this.querySelectorAll('[data-moodi-embroidery-toggle]'));
        this.fields = this.querySelector('[data-moodi-embroidery-fields]');
        this.textInput = this.querySelector('[data-moodi-embroidery-text]');
        this.countEl = this.querySelector('[data-moodi-embroidery-count]');
        this.locationSelect = this.querySelector('[data-moodi-embroidery-location]');
        this.errorEl = this.querySelector('[data-moodi-embroidery-error]');
        this.charLimit = parseInt(this.dataset.charLimit, 10) || 12;

        this.productForm = this.closest('product-form') || document.getElementById(this.dataset.productFormId);
        this.form = this.productForm ? this.productForm.querySelector('form') : this.closest('form');

        this.toggles.forEach((toggle) => {
          toggle.addEventListener('change', this.onToggleChange.bind(this));
        });

        if (this.textInput) {
          this.textInput.addEventListener('input', this.onTextInput.bind(this));
        }

        if (this.form) {
          // Capture phase so this runs before product-form's own submit
          // handler (which calls evt.preventDefault() unconditionally
          // and then fetches) — returning early here still lets that
          // handler run for a *valid* submission; we only ever block
          // by leaving our own error visible and re-focusing the field,
          // never by fighting product-form's own fetch/loading state.
          this.form.addEventListener('submit', this.onFormSubmit.bind(this), true);
        }

        this.updateFieldsVisibility();
        this.updateCount();

        // Only wire the paid-addon flow if this product actually has
        // one configured. Most products will not, and in that case
        // this element does nothing beyond the free text/placement
        // properties, which ride the main add-to-cart request for free.
        if (this.dataset.addonVariantId) {
          this.bindAddonFlow();
        }
      }

      get isEmbroiderySelected() {
        const checked = this.toggles.find((t) => t.checked);
        return !!checked && checked.value === 'embroidery';
      }

      onToggleChange() {
        this.updateFieldsVisibility();
        this.clearError();
      }

      updateFieldsVisibility() {
        if (!this.fields) return;
        const on = this.isEmbroiderySelected;
        this.fields.hidden = !on;

        if (this.textInput) {
          this.textInput.required = on;
          // Disabled form controls are excluded from FormData by the
          // browser itself (product-form.js builds the Add to Bag
          // request with `new FormData(this.form)`, and this input is
          // only form-associated via the `form="..."` attribute, not
          // DOM-nested — so `hidden` on the wrapping div alone does
          // NOT keep it out of that FormData). Disabling it here is
          // what actually guarantees it can never ride a submission
          // while personalisation is off, on top of clearing the
          // value so re-enabling always starts from a clean slate.
          this.textInput.disabled = !on;
          if (!on) this.textInput.value = '';
        }

        if (this.locationSelect) {
          this.locationSelect.required = on;
          this.locationSelect.disabled = !on;
          if (!on) this.locationSelect.selectedIndex = 0;
        }

        // Keep the visible counter in sync with whatever we just did
        // to textInput.value (cleared when off, unchanged when on).
        this.updateCount();
      }

      onTextInput() {
        this.updateCount();
        if (this.textInput.value.trim() !== '') this.clearError();
      }

      updateCount() {
        if (!this.textInput || !this.countEl) return;
        const length = this.textInput.value.length;
        this.countEl.textContent = `${length} / ${this.charLimit}`;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        if (this.textInput) this.textInput.removeAttribute('aria-invalid');
      }

      onFormSubmit(evt) {
        if (!this.isEmbroiderySelected) return;
        const value = this.textInput ? this.textInput.value.trim() : '';
        if (value !== '') return;

        // Block submission: embroidery selected but no text entered.
        // product-form's own handler is registered in the bubble phase
        // and runs after this, so stopping propagation here prevents
        // the add-to-cart fetch from firing at all for this attempt.
        evt.preventDefault();
        evt.stopImmediatePropagation();

        if (this.errorEl) this.errorEl.hidden = false;
        if (this.textInput) {
          this.textInput.setAttribute('aria-invalid', 'true');
          this.textInput.focus();
        }
      }

      bindAddonFlow() {
        try {
          if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.cartUpdate) {
            return;
          }
        } catch (e) {
          return;
        }

        this._pendingAddonAdd = false;

        this.form.addEventListener(
          'submit',
          () => {
            this._pendingAddonAdd = this.isEmbroiderySelected;
          },
          true
        );

        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source !== 'product-form' || !this._pendingAddonAdd) return;
          this._pendingAddonAdd = false;
          this.addAddonLine();
        });
      }

      addAddonLine() {
        const variantId = this.dataset.addonVariantId;
        if (!variantId) return;

        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: {
            Personalisation: 'Embroidery add-on',
          },
        });

        fetch(`${window.routes ? window.routes.cart_add_url : '/cart/add.js'}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body,
        })
          .then((response) => response.json())
          .then(() => {
            const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (!cartEl || typeof cartEl.getSectionsToRender !== 'function') return;
            const sectionIds = cartEl.getSectionsToRender().map((section) => section.id);
            const params = new URLSearchParams();
            sectionIds.forEach((id) => params.append('sections', id));
            return fetch(`${window.routes ? window.routes.cart_url : '/cart'}?${params.toString()}`)
              .then((r) => r.json())
              .then((cartResponse) => cartEl.renderContents(cartResponse));
          })
          .catch((e) => {
            // Non-fatal: the primary garment line has already been
            // added successfully. Surfacing a hard error here would
            // read as the whole add-to-cart having failed, when it
            // didn't — so this only logs, it never blocks the UI.
            console.error('MOODI embroidery add-on line failed to add:', e);
          });
      }
    }
  );
}

if (!customElements.get('moodi-config-summary')) {
  customElements.define(
    'moodi-config-summary',
    class MoodiConfigSummary extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.productFormId);

        // <variant-selects> (Edition + Size) and <moodi-embroidery-panel>
        // are separate sibling BLOCKS under <product-info> (see
        // sections/main-product.liquid) — NOT DOM descendants of <form>.
        // Their inputs are only cross-linked to the form via the
        // form="..." attribute (so they still submit with it), and that
        // attribute link does not make their events bubble to the form
        // element. So `this.form.addEventListener('change', ...)` never
        // actually fires for a Size/Edition/embroidery input — the
        // previous version of this element relied on that and was
        // therefore permanently stuck showing its initial-load values.
        // <product-info> genuinely IS an ancestor of all of these inputs
        // in the DOM, so listen there instead.
        this.productInfo = this.closest('product-info');

        this.editionValue = this.querySelector('[data-value="edition"]');
        this.sizeValue = this.querySelector('[data-value="size"]');
        this.personalisationValue = this.querySelector('[data-value="personalisation"]');
        this.placementValue = this.querySelector('[data-value="placement"]');
        this.placementRow = this.querySelector('[data-row="placement"]');

        this.boundRefresh = this.refresh.bind(this);
        this.unsubscribers = [];

        if (this.productInfo) {
          this.productInfo.addEventListener('change', this.boundRefresh);
          this.productInfo.addEventListener('input', this.boundRefresh);
        }

        // Belt-and-braces: also hook Dawn's own authoritative pub/sub
        // signals (assets/global.js + assets/product-info.js), so this
        // keeps working even across the section re-render Dawn performs
        // on every option change. That re-render REPLACES the whole
        // <variant-selects> element (assets/product-info.js →
        // updateOptionValues → HTMLUpdateUtility.viewTransition) rather
        // than mutating it in place, which is why a MutationObserver on
        // any one node inside it (the previous approach here) goes stale
        // after the very first change and never fires again.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          if (PUB_SUB_EVENTS.optionValueSelectionChange) {
            this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.boundRefresh));
          }
          if (PUB_SUB_EVENTS.variantChange) {
            this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, this.boundRefresh));
          }
        }

        this.refresh();
      }

      disconnectedCallback() {
        if (this.productInfo) {
          this.productInfo.removeEventListener('change', this.boundRefresh);
          this.productInfo.removeEventListener('input', this.boundRefresh);
        }
        this.unsubscribers.forEach((unsubscribe) => unsubscribe && unsubscribe());
      }

      // Re-queried live every time, deliberately never cached: Dawn
      // replaces this element wholesale on option changes, so a
      // reference captured once at connectedCallback would go stale.
      getVariantSelects() {
        return this.productInfo ? this.productInfo.querySelector('variant-selects') : null;
      }

      refresh() {
        this.refreshEditionAndSize();
        this.refreshPersonalisation();
      }

      refreshEditionAndSize() {
        const variantSelects = this.getVariantSelects();
        if (!variantSelects) return;

        if (this.hasAttribute('data-has-edition')) {
          const editionInput = variantSelects.querySelector('[name^="Edition-"]:checked');
          this.editionValue.textContent = editionInput ? editionInput.value : '—';
        }

        if (this.hasAttribute('data-has-size')) {
          // Read the currently checked/selected value(s) directly from
          // the live inputs — the same authoritative source Dawn's own
          // variant-selects component reads from (see
          // VariantSelects.selectedOptionValues in assets/global.js) —
          // rather than parsing the [data-selected-variant] JSON island,
          // which only reflects whatever the *last completed* section
          // fetch resolved to and would leave this a step behind the
          // browser's own, instantly-updated radio state.
          const nodes = Array.from(
            variantSelects.querySelectorAll('select option[selected], fieldset input:checked')
          );
          const values = nodes
            .filter((el) => {
              const name = el.tagName === 'OPTION' ? el.closest('select')?.name || '' : el.name || '';
              return !name.startsWith('Edition-');
            })
            .map((el) => el.value)
            .filter(Boolean);
          this.sizeValue.textContent = values.length ? values.join(' / ') : '—';
        }
      }

      refreshPersonalisation() {
        if (!this.hasAttribute('data-has-personalisation') || !this.productInfo) return;

        const toggle = this.productInfo.querySelector('[data-moodi-embroidery-toggle]:checked');
        const isEmbroidery = !!toggle && toggle.value === 'embroidery';
        const text = this.productInfo.querySelector('[data-moodi-embroidery-text]');
        const location = this.productInfo.querySelector('[data-moodi-embroidery-location]');

        if (isEmbroidery && text && text.value.trim() !== '') {
          this.personalisationValue.textContent = text.value.trim();
          if (this.placementRow && location) {
            this.placementRow.hidden = false;
            this.placementValue.textContent = location.options[location.selectedIndex]
              ? location.options[location.selectedIndex].value
              : '—';
          }
        } else {
          this.personalisationValue.textContent = 'None';
          if (this.placementRow) this.placementRow.hidden = true;
        }
      }
    }
  );
}
